import { trackFieldSelected } from '@/lib/analytics/scribe/ui';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type DemoExperienceVariant } from '@/models/analytics/features/onboarding-hubspot';
import { getCountryConfigEventProps } from '@/utils/country-config';
import { type WebinarRegion } from '@/utils/onboardingExperience';
import { FieldType, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useCallback, useEffect, useRef } from 'react';

interface UseDemoOptInTrackingParams {
  demoExperienceVariant: DemoExperienceVariant;
  webinarRegion: WebinarRegion | undefined;
  /** Country name used for the `field_selected` payload on sdr-demo opt-in. */
  country?: string;
  /** ISO alpha-2 country code used to resolve the allotted `country-config`. */
  countryCode?: string;
  /** Locale used for the `field_selected` payload on sdr-demo opt-in. */
  language?: string;
}

/**
 * Returns analytics callbacks for the demo opt-in card (webinar/sdr-demo
 * variants). Use inside the card itself so any flow that mounts the card —
 * V2 CompletionStep, LearnBasics ClinicStep — gets opt-in/timeslot tracking
 * with no extra wiring.
 */
export function useDemoOptInTracking({
  demoExperienceVariant,
  webinarRegion,
  country,
  language,
}: UseDemoOptInTrackingParams) {
  const trackOptInChanged = useCallback(
    (optedIn: boolean) => {
      if (demoExperienceVariant === 'webinar') {
        track({
          event_name: TrackingEventName.WebinarOptInChanged,
          props: { opted_in: optedIn, region: webinarRegion },
        });
      } else if (demoExperienceVariant === 'sdr-demo') {
        track({
          event_name: TrackingEventName.SdrDemoOptInChanged,
          props: { opted_in: optedIn },
        });
        // DEV-11979: control event for the phone-collection demo flow.
        // Fires when the user toggles the "book a demo" opt-in checkbox.
        // Paired with `reason_for_demo_phone` field_selected (text), which
        // fires from `RequestDemoPhoneAndReasonFields` as the user types in
        // the reason input that appears below.
        if (optedIn) {
          trackFieldSelected({
            fieldName: 'book_a_demo_opt_in',
            fieldType: FieldType.Checkbox,
            screenName: ScreenName.OnboardingUserSurvey,
            country,
            language,
          });
        }
      }
    },
    [demoExperienceVariant, webinarRegion, country, language]
  );

  const trackTimeslotSelected = useCallback(
    (timeslotLabel: string, hubspotProperty: string) => {
      track({
        event_name: TrackingEventName.WebinarTimeslotSelected,
        props: {
          timeslot_label: timeslotLabel,
          hubspot_property: hubspotProperty,
          region: webinarRegion,
        },
      });
    },
    [webinarRegion]
  );

  return { trackOptInChanged, trackTimeslotSelected };
}

type UseCompletionStepAnalyticsParams = UseDemoOptInTrackingParams;

/**
 * Onboarding demo decision-surface impression hook. Fires
 * `OnboardingDemoVariantViewed` exactly once per mount and returns the same
 * opt-in/timeslot tracking callbacks as `useDemoOptInTracking`. Mounted by both
 * the V2 CompletionStep and the LearnBasics PrivacyStep so variant performance
 * is comparable across flows.
 */
export function useCompletionStepAnalytics({
  demoExperienceVariant,
  webinarRegion,
  country,
  countryCode,
  language,
}: UseCompletionStepAnalyticsParams) {
  // Impression event — fire exactly once per mount (prevents the double-fire
  // that React 18 Strict Mode causes, and avoids re-firing on later renders).
  const hasFiredVariantViewed = useRef(false);

  useEffect(() => {
    if (hasFiredVariantViewed.current) return;
    hasFiredVariantViewed.current = true;
    track({
      event_name: TrackingEventName.OnboardingDemoVariantViewed,
      props: {
        variant: demoExperienceVariant,
        // Country is already selected by the time the demo surface mounts.
        ...getCountryConfigEventProps(countryCode, language),
      },
    });
  }, [demoExperienceVariant, countryCode, language]);

  return useDemoOptInTracking({
    demoExperienceVariant,
    webinarRegion,
    country,
    countryCode,
    language,
  });
}
